<template>
  <div v-if="request">
    <h1>Payment request</h1>
    <v-alert :value="true" type="info">
      <span>This payment relates to the file mentioned below.</span>
    </v-alert>
    <v-card v-if="file && category">
      <v-card-text>
        <h2>File details</h2>
        <v-layout row>
          <v-flex xs12 md5>
            <strong>File reference</strong>
          </v-flex>
          <v-flex xs12 md7>
            <FileZ :file="file" />
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs12 md5>
            <strong>Category</strong>
          </v-flex>
          <v-flex xs12 md7>{{category.name}}</v-flex>
        </v-layout>
        <v-layout row v-if="clients">
          <v-flex xs12 md5>
            <strong>Client names</strong>
          </v-flex>
          <v-flex xs12 md7>
            <EntityZ v-for="entity in clients" :key="entity.id" :entity="entity" />
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
    <v-alert :value="true" type="info">
      <span>Please review the payment details below.</span>
    </v-alert>
    <v-card>
      <v-card-text>
        <h2>Request details</h2>
        <v-layout row>
          <v-flex xs12 md5>
            <strong>Date of request</strong>
          </v-flex>
          <v-flex xs12 md7>{{request.created | formatDateTime}}</v-flex>
        </v-layout>
        <v-layout row v-if="user">
          <v-flex xs12 md5>
            <strong>Requested by</strong>
          </v-flex>
          <v-flex xs12 md7>
            <span v-if="user">
              <EntityZ :entity="user" />
            </span>
            <span v-if="!user">{{request.userid}}</span>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs12 md5>
            <strong>Purpose</strong>
          </v-flex>
          <v-flex xs12 md7>
            <span v-if="purpose">{{purpose.body}}</span>
            <span v-if="!purpose">{{request.purposeid}}</span>
          </v-flex>
        </v-layout>
        <v-layout row v-if="request.note">
          <v-flex xs12 md5>
            <strong>Note</strong>
          </v-flex>
          <v-flex xs12 md7>{{request.note}}</v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
    <v-alert :value="true" type="info">
      <span>Highlight below the credit or debit card which will be used to make payment.</span>
    </v-alert>
    <v-form ref="paymentRequestForm">
      <v-card>
        <v-card-text>
          <h2>Card</h2>
          <v-radio-group v-model="cardid" :mandatory="false">
            <v-radio v-for="card in cards" :key="card.id" :value="card.id">
              <template slot="label">
                <span v-if="card.issuer">{{card.issuer.name}}</span>
                <span>&#160;</span>
                <span>{{card.number}}</span>
                <span>&#160;</span>
                <span>{{card.expire}}</span>
                <span>&#160;</span>
                <span>{{card.cardType}}</span>
              </template>
            </v-radio>
          </v-radio-group>
          <v-alert :value="!cards || cards.length == 0" type="error">
            <span>There are no available payment methods. Please add a card to a client on the file.</span>
          </v-alert>
        </v-card-text>
      </v-card>
      <!-- <h:link outcome="card.xhtml" value="#{msg.customer3}">
				<f:param name="customerId" value="#{loginBean.userId}" />
      </h:link>-->
      <v-alert :value="true" type="warning">
        <span>Review payment amount below before final payment authorisation.</span>
        <span
          v-if="cardType == 'CREDIT'"
        >If your designated card is a credit card you will be charged a 2.5% handling fee.</span>
        <span
          v-if="cardType != 'CREDIT'"
        >If your card is a debit card you will not be charged a handling fee.</span>
      </v-alert>
      <v-card>
        <v-card-text>
          <h2>Payment details</h2>
          <v-select
            v-model="cardType"
            :items="cardTypes"
            item-value="id"
            item-text="body"
            label="Card type"
            outline
            v-if="!cardTypeSelected"
          />
          <v-layout row v-if="cardTypeSelected">
            <v-flex xs12 md5>
              <strong>Card type</strong>
            </v-flex>
            <v-flex xs12 md7>{{cardTypeSelected}}</v-flex>
          </v-layout>
          <v-layout row>
            <v-flex xs12 md5>
              <strong>Amount</strong>
            </v-flex>
            <v-flex xs12 md7>{{request.amount | toCurrency}}</v-flex>
          </v-layout>
          <v-layout row>
            <v-flex xs12 md5>
              <strong>Handling fee</strong>
              <span v-if="cardTypeSelected == 'CREDIT'">&#160;(charged at 2.5%)</span>
            </v-flex>
            <v-flex xs12 md7>
              <span v-if="cardTypeSelected == 'CREDIT'">{{surcharge.surcharge | toCurrency}}</span>
              <span v-if="cardTypeSelected != 'CREDIT'">No handling fee for debit cards</span>
            </v-flex>
          </v-layout>
          <v-layout row>
            <v-flex xs12 md5>
              <strong>Total to be collected</strong>
            </v-flex>
            <v-flex xs12 md7>{{surcharge.total | toCurrency}}</v-flex>
          </v-layout>
        </v-card-text>
      </v-card>
      <v-alert :value="true" type="info">
        <span>Click "Authorise Payment" to authorise NBM to deduct this amount from the designated payment method.</span>
      </v-alert>
      <div>
        <v-checkbox
          v-model="surcharge.surchargeAgree"
          label="Please check the box to confirm that you agree to make the payment via your preferred payment method"
          :rules="valueRules"
        />
      </div>
      <v-btn style="margin:20px;" @click="authorisePayment" :disabled="!cardid" color="success">
        <v-icon>done_outline</v-icon>&#160;Authorise Payment
      </v-btn>
    </v-form>
  </div>
</template>

<script>
import EntityZ from "@/components/link/EntityZ";
import FileZ from "@/components/link/FileZ";
import nbmlaw from "@/api/nbmlaw";
export default {
  name: "Request",
  metaInfo() {
    return {
      title: "Payment request",
      titleTemplate: "%s - Payments - Conveyancing Tracker",
      htmlAttrs: {
        lang: "en",
        amp: true
      }
    };
  },
  components: { EntityZ, FileZ },
  computed: {
    modifiedCards() {
      if (!this.cards) {
        return undefined;
      }
      return this.cards.map(c => {
        const issuer =
          this.issuers && this.issuers[c.issuerid]
            ? this.issuers[c.issuerid]
            : undefined;
        return {
          ...c,
          issuer
        };
      });
    },
    surcharge() {
      // calculate surcharge
      const amount = this.request.amount;
      const surchargePercent = this.surchargePercent;
      const surcharge = (amount * surchargePercent) / 100;
      // calculate total including surcharge
      const total = amount + surcharge;
      return {
        amount,
        surchargePercent,
        surcharge,
        total,
        surchargeAgree: this.surchargeAgree
      };
    }
  },
  data() {
    return {
      file: null,
      clients: null,
      user: null,
      category: null,
      request: null,
      purpose: null,
      cards: null,
      issuers: null,
      cardid: null,
      cardType: null,
      cardTypeSelected: null,
      surchargeAgree: false,
      surchargePercent: null,
      valueRules: [v => !!v || "Value is required"],
      cardTypes: [
        { id: "CREDIT", body: "Credit" },
        { id: "DEBIT", body: "Debit" }
      ]
    };
  },
  methods: {
    fetchRequest() {
      nbmlaw
        .get(`/cors/admin/payment/requests/${this.$route.params.id}`)
        .then(response => {
          // console.log(response.data);
          this.request = response.data;
          this.fetchUser();
          this.fetchFile();
          this.fetchClients();
          this.fetchPurpose();
        });
    },
    fetchFile() {
      nbmlaw
        .get(`/cors/admin/file/files/${this.request.fileid}`)
        .then(response => {
          // console.log(response.data);
          this.file = response.data;
          this.fetchCategory();
        });
    },
    fetchClients() {
      nbmlaw
        .get(`/cors/admin/file/files/${this.request.fileid}/clients/false`)
        .then(response => {
          // console.log(response.data);
          this.clients = response.data;
          this.fetchCards();
        });
    },
    fetchCategory() {
      nbmlaw
        .get(`/cors/admin/file/categories/${this.file.categoryId}`)
        .then(response => {
          // console.log(response.data);
          this.category = response.data;
        });
    },
    fetchPurpose() {
      nbmlaw
        .get(`/cors/admin/payment/purposes/${this.request.purposeid}`)
        .then(response => {
          // console.log(response.data);
          this.purpose = response.data;
        });
    },
    fetchUser() {
      nbmlaw
        .get(`/cors/admin/directory/entities/${this.request.userid}`)
        .then(response => {
          // console.log(response.data);
          this.user = response.data;
        })
        .catch(() => {});
    },
    fetchCards() {
      const ids = [...new Set(this.clients.map(c => c.id))];
      if (ids.length > 0) {
        const ids_ = ids.join(".");
        nbmlaw
          .get(`/cors/admin/directory/entities/multiple/${ids_}/cards`)
          .then(response => {
            // console.log(response.data);
            this.cards = response.data;
            if (this.cards && this.cards.length > 0) {
              this.cardid = this.cards[0].id;
              this.cardTypeSelected = this.cards[0].cardType;
              this.cardType = this.cards[0].cardType;
            }
          })
          .catch(() => {});
      }
    },
    fetchIssuers() {
      nbmlaw
        .get(`/cors/admin/payment/issuers`)
        .then(response => {
          // console.log(response.data);
          this.issuers = response.data;
        })
        .catch(() => {});
    },
    authorisePayment() {
      if (this.$refs.paymentRequestForm.validate()) {
        nbmlaw
          .put(
            `/cors/admin/payment/requests/${this.$route.params.id}/authorise`,
            {
              cardType: this.cardType,
              cardid: this.cardid,
              surcharge: this.surcharge
            }
          )
          .then(() => {
            // console.log(response.data);
            this.$router.push({
              name: "file",
              params: { id: this.request.fileid }
            });
          })
          .catch(() => {});
      }
    }
  },
  created() {
    this.fetchRequest();
    this.fetchIssuers();
  }
};
</script>